import { Table } from "@mantine/core";

import { Fragment } from "react/jsx-runtime";

import { LabFlowSheetSection } from "@/health-gorilla/types/flow-sheet";
import { formatGestationalWeeksForLabFlowSheet } from "@/health-gorilla/utils/labs";
import { usDateFormat } from "@/utils/datetime";

import useStickyLabFlowSheetTable from "./hooks/useStickyLabFlowSheetTable";

const COLUMN_WIDTH = 180;
const FIRST_COLUMN_WIDTH = 240;

interface ClientLabFlowSheetTableProps {
  collectionDates: string[];
  edd: string | undefined;
  sections: LabFlowSheetSection[];
}

export default function ClientLabFlowSheetTable({
  collectionDates,
  edd,
  sections,
}: ClientLabFlowSheetTableProps) {
  const {
    firstHeaderRowRef,
    baseCellStyle,
    stickyColumnStyle,
    stickyFirstRowStyle,
    stickySecondRowStyle,
    stickyCornerFirstRowStyle,
    stickyCornerSecondRowStyle,
  } = useStickyLabFlowSheetTable(COLUMN_WIDTH);

  return (
    <Table.ScrollContainer
      type="native"
      minWidth={1200}
      style={{
        borderRadius: 8,
        border: "1px solid var(--mantine-color-cream-4)",
        maxHeight: "calc(100vh - var(--mantine-spacing-xl) * 6)",
        overflowY: "auto",
      }}
    >
      <Table
        verticalSpacing="sm"
        horizontalSpacing="sm"
        style={{
          tableLayout: "fixed",
          borderCollapse: "separate",
          borderSpacing: 0,
        }}
        styles={{
          th: {
            textAlign: "center",
            ...baseCellStyle,
          },
          td: {
            textAlign: "center",
            ...baseCellStyle,
          },
        }}
      >
        <Table.Thead>
          <Table.Tr ref={firstHeaderRowRef} style={stickyFirstRowStyle}>
            <Table.Th
              style={{
                ...stickyCornerFirstRowStyle,
                width: FIRST_COLUMN_WIDTH,
              }}
            >
              Weeks
            </Table.Th>
            {collectionDates.map((date) => (
              <Table.Th key={date} style={stickyFirstRowStyle}>
                {formatGestationalWeeksForLabFlowSheet(edd, date)}
              </Table.Th>
            ))}
            <Table.Th style={stickyFirstRowStyle}>Units</Table.Th>
            <Table.Th style={stickyFirstRowStyle}>Reference</Table.Th>
          </Table.Tr>
          <Table.Tr fw="bold" style={stickySecondRowStyle}>
            <Table.Th style={stickyCornerSecondRowStyle}>Date Collected</Table.Th>
            {collectionDates.map((date) => (
              <Table.Th key={date}>{usDateFormat(date)}</Table.Th>
            ))}
            <Table.Th
              style={{
                ...stickySecondRowStyle,
                backgroundColor: "var(--mantine-color-gray-0)",
              }}
            />
            <Table.Th
              style={{
                ...stickySecondRowStyle,
                backgroundColor: "var(--mantine-color-gray-0)",
              }}
            ></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sections.map((section) => (
            <Fragment key={section.code}>
              {/* Section Header */}
              <Table.Tr>
                <Table.Td
                  fw="bold"
                  style={{
                    ...stickyColumnStyle,
                    backgroundColor: "var(--mantine-color-cream-3)",
                    borderRight: "none",
                  }}
                >
                  {section.name}
                </Table.Td>
                <Table.Td bg="cream.3" style={baseCellStyle} colSpan={collectionDates.length + 2} />
              </Table.Tr>
              {/* Subsection Rows */}
              {section.subsections.map((subsection) => (
                <Table.Tr key={`${subsection.code}-${subsection.name}`}>
                  <Table.Td fw="bold" style={stickyColumnStyle}>
                    {subsection.name}
                  </Table.Td>
                  {collectionDates.map((date) => (
                    <Table.Td key={date}>{subsection.values[date] ?? ""}</Table.Td>
                  ))}
                  <Table.Td bg="gray.0">{subsection.unit}</Table.Td>
                  <Table.Td bg="gray.0">{subsection.referenceRange}</Table.Td>
                </Table.Tr>
              ))}
            </Fragment>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
